const CONFIG = {
  DELIMITERS: ['[[', ']]'],
  ARTICLES_PER_PAGE_MOBILE: 6,
  ARTICLES_PER_PAGE_DESKTOP: 6,
  SWIPER_THRESHOLD: 15,
  SEARCH_DEBOUNCE_DELAY: 300,
  MOBILE_BREAKPOINT: 767,
  DEFAULT_PAGINATION_RESET: 1,
  // The hero article is excluded automatically from its banner link; this is only for extra one-offs.
  EXCLUDED_ARTICLE_HANDLES: []
};

window.BlogApp = {
  name: 'BlogDataApp',
  delimiters: CONFIG.DELIMITERS,

  data: {
    isShown: false,
    loading: true,
    error: null,
    blogData: null,
    articles: [],
    displayedArticles: [],
    articlesPerPage: CONFIG.ARTICLES_PER_PAGE_DESKTOP,
    currentPage: 1,
    availableBlogs: [],
    blogTitle: 'Blog Articles',
    blogDescription: '',
    selectedCategory: null,
    selectedTopic: null,
    selectedCategories: [],
    selectedTopics: [],
    searchQuery: '',
    filtersExpanded: false,
    sortExpanded: false,
    sortBy: 'newest',
    swiperThreshold: CONFIG.SWIPER_THRESHOLD,
    searchDebounceTimer: null,
  },

  computed: {
    // Handles kept out of the grid: the hero article derived from the banner link in
    // templates/blog.liquid, plus any manual entries in CONFIG.EXCLUDED_ARTICLE_HANDLES.
    excludedArticleHandles() {
      const excludedHandles = (CONFIG.EXCLUDED_ARTICLE_HANDLES || []).map(h => h.toLowerCase().trim());

      // A hero pointing anywhere other than an article (a product, say) excludes nothing.
      const heroLink = (this.heroArticleLink || '').toLowerCase().trim();
      const heroMatch = heroLink.match(/\/blogs\/([^/?#]+)\/([^/?#]+)/);
      if (heroMatch) {
        excludedHandles.push(heroMatch[1] + '/' + heroMatch[2], heroMatch[2]);
      }

      return excludedHandles;
    },

    // Every list, count and filter option reads from this, so the hero never leaks back in.
    listedArticles() {
      const excludedHandles = this.excludedArticleHandles;
      if (excludedHandles.length === 0) return this.articles;

      return this.articles.filter(article => {
        const handle = (article.handle || '').toLowerCase().trim();
        return !excludedHandles.includes(handle);
      });
    },

    filteredArticles() {
      let filteredArticles = this.listedArticles.slice();

      if (this.searchQuery && this.searchQuery.trim()) {
        const searchTerm = this.searchQuery.toLowerCase().trim();
        filteredArticles = filteredArticles.filter(article => {
          if (article.title && article.title.toLowerCase().includes(searchTerm)) {
            return true;
          }

          if (article.tags && Array.isArray(article.tags)) {
            for (const tag of article.tags) {
              if (tag.toLowerCase().includes(searchTerm)) {
                return true;
              }
            }
          }

          const contentFields = [article.excerpt, article.summary, article.content];
          return contentFields.some(field => field && field.toLowerCase().includes(searchTerm));
        });
      }

      const allSelectedCategories = [
        ...this.selectedCategories,
        ...(this.selectedCategory ? [this.selectedCategory] : [])
      ];

      if (allSelectedCategories.length > 0) {
        filteredArticles = filteredArticles.filter(article => {
          if (!article.tags) return false;
          return allSelectedCategories.some(category =>
            article.tags.includes(`category:${category}`)
          );
        });
      }

      const allSelectedTopics = [
        ...this.selectedTopics,
        ...(this.selectedTopic ? [this.selectedTopic] : [])
      ];

      if (allSelectedTopics.length > 0) {
        filteredArticles = filteredArticles.filter(article => {
          if (!article.tags) return false;
          return allSelectedTopics.some(topic =>
            article.tags.includes(`topic:${topic}`)
          );
        });
      }

      return this.sortArticles(filteredArticles);
    },

    hasActiveFilters: function() {
      return (this.selectedCategories && this.selectedCategories.length > 0) ||
        (this.selectedTopics && this.selectedTopics.length > 0) ||
        (this.searchQuery && this.searchQuery.trim());
    },

    canLoadMore: function() {
      return this.displayedArticles.length < this.filteredArticles.length;
    },

    canShowLess: function() {
      return this.currentPage > 1 && this.displayedArticles.length >= this.filteredArticles.length;
    },

    totalArticles: function() {
      return this.listedArticles.length;
    },

    availableCategories() {
      const categories = new Set();
      this.listedArticles.forEach(article => {
        if (article.tags && Array.isArray(article.tags)) {
          article.tags.forEach(tag => {
            if (tag.startsWith('category:')) {
              const category = tag.replace('category:', '');
              if (category) categories.add(category);
            }
          });
        }
      });
      return Array.from(categories).sort();
    },

    availableTopics() {
      const topics = new Set();
      this.listedArticles.forEach(article => {
        if (article.tags && Array.isArray(article.tags)) {
          article.tags.forEach(tag => {
            if (tag.startsWith('topic:')) {
              const topic = tag.replace('topic:', '');
              if (topic) topics.add(topic);
            }
          });
        }
      });
      return Array.from(topics).sort();
    },

    orderedAvailableTopics() {
      const desiredOrder = ['lift', 'hybrid', 'stretch', 'living'];
      const topics = this.availableTopics;
      const ordered = [];

      desiredOrder.forEach(topic => {
        if (topics.includes(topic)) {
          ordered.push(topic);
        }
      });

      topics.forEach(topic => {
        if (!desiredOrder.includes(topic)) {
          ordered.push(topic);
        }
      });

      return ordered;
    },


    currentArticlesPerPage() {
      return this.isMobile() ? CONFIG.ARTICLES_PER_PAGE_MOBILE : CONFIG.ARTICLES_PER_PAGE_DESKTOP;
    },


    progressPercentage: function() {
      if (this.filteredArticles.length === 0) return 0;
      return Math.round((this.displayedArticles.length / this.filteredArticles.length) * 100);
    },

    shouldShowSwiper: function() {
      return this.filteredArticles.length > 0;
    },

    swiperArticles() {
      const sortedArticles = this.listedArticles.slice().sort((a, b) => {
        return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
      });
      return sortedArticles.slice(0, 5);
    }
  },

  methods: {

    loadBlog(handle) {
      this.loading = true;
      this.error = null;
      this.articles = [];
      this.displayedArticles = [];
      this.currentPage = 1;
      this.selectedCategory = null;
      this.selectedTopic = null;

      const searchUrl = `/search?view=blogs-data&q=${encodeURIComponent(handle)}`;

      axios.get(searchUrl)
        .then(response => {
          if (response.data.error) {
            this.error = response.data.error;
            this.availableBlogs = response.data.available_blogs || [];
            this.isShown = true;
          } else {
            this.processBlogData(response.data);
          }
        })
        .catch(error => {
          console.error('Error loading blog data:', error);
          this.error = 'Failed to load blog data. Please try again.';
          this.isShown = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    processBlogData(data) {
      this.blogData = data.blog;
      this.articles = data.articles || [];


      this.blogTitle = data.blog.title || 'Blog Articles';
      this.blogDescription = data.blog.description || '';
      this.updateDisplayedArticles();

      if (this.articles.length === 0) {
        this.error = 'No articles found for this blog.';
      }

      this.isShown = true;
    },

    updateDisplayedArticles() {
      const endIndex = this.currentPage * this.currentArticlesPerPage;
      this.displayedArticles = this.filteredArticles.slice(0, endIndex);
    },

    loadMore() {
      this.currentPage++;
      this.updateDisplayedArticles();
    },

    showLess: function() {
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    filterByCategory: function(category) {
      this.selectedCategory = category;
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    clearCategoryFilter: function() {
      this.selectedCategory = null;
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    filterByTopic: function(topic) {
      this.selectedTopic = topic;
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    clearTopicFilter: function() {
      this.selectedTopic = null;
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    clearAllFilters: function() {
      this.selectedCategory = null;
      this.selectedTopic = null;
      this.selectedCategories = [];
      this.selectedTopics = [];
      this.searchQuery = '';
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    toggleFilters: function() {
      if (this.sortExpanded) {
        this.sortExpanded = false;
      }
      this.filtersExpanded = !this.filtersExpanded;
    },

    toggleSort: function() {
      if (this.filtersExpanded) {
        this.filtersExpanded = false;
      }
      this.sortExpanded = !this.sortExpanded;
    },

    updateFilters() {
      this.currentPage = CONFIG.DEFAULT_PAGINATION_RESET;
      this.updateDisplayedArticles();
    },

    removeCategory(category) {
      const index = this.selectedCategories.indexOf(category);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
        this.updateFilters();
      }
    },

    removeTopic(topic) {
      const index = this.selectedTopics.indexOf(topic);
      if (index > -1) {
        this.selectedTopics.splice(index, 1);
        this.updateFilters();
      }
    },

    updateSort() {
      this.currentPage = CONFIG.DEFAULT_PAGINATION_RESET;
      this.updateDisplayedArticles();
    },

    toggleSortOption: function(option) {
      if (this.sortBy === option) {
        this.sortBy = null;
      } else {
        this.sortBy = option;
      }
      this.updateSort();
    },

    sortArticles(articles) {
      if (!this.sortBy) return articles.slice();

      const sorted = articles.slice();

      if (this.sortBy === 'newest') {
        sorted.sort((a, b) => {
          return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
        });
      } else if (this.sortBy === 'oldest') {
        sorted.sort((a, b) => {
          return new Date(a.published_at || a.created_at) - new Date(b.published_at || b.created_at);
        });
      }

      return sorted;
    },

    searchArticles: function() {
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    clearSearch: function() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.updateDisplayedArticles();
    },

    formatDate: function(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '';
      }
      return date.toLocaleDateString(this.currentLang || 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    stripHtml(html) {
      if (!html) return '';
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    },

    formatFilterText(text) {
      if (!text) return '';
      return text.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    saveFilters: function() {
      this.updateFilters();
      this.toggleFilters();
    },

    isMobile() {
      return window.innerWidth <= CONFIG.MOBILE_BREAKPOINT;
    },

    debouncedSearch(searchQuery) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchQuery = searchQuery;
        this.currentPage = CONFIG.DEFAULT_PAGINATION_RESET;
        this.updateDisplayedArticles();
      }, CONFIG.SEARCH_DEBOUNCE_DELAY);
    },

    handleFilterChange() {
      if (!this.isMobile()) {
        this.updateFilters();
      }
    },

    lockBodyScroll: function() {
      document.body.classList.add('oa-blog-filter-open');
    },

    unlockBodyScroll: function() {
      document.body.classList.remove('oa-blog-filter-open');
    },

    addEscapeListener: function() {
      document.addEventListener('keydown', this.handleEscapeKey);
    },

    removeEscapeListener: function() {
      document.removeEventListener('keydown', this.handleEscapeKey);
    },

    handleEscapeKey: function(event) {
      if (event.key === 'Escape' && this.filtersExpanded) {
        this.toggleFilters();
      }
    },

    parseUrlParameters: function() {
      const urlParams = new URLSearchParams(window.location.search);
      const topicParam = urlParams.get('topic');

      if (topicParam && !this.selectedTopics.includes(topicParam)) {
        this.selectedTopics.push(topicParam);
        this.currentPage = CONFIG.DEFAULT_PAGINATION_RESET;
      }
    }

  },

  watch: {
    blogHandle: function(newHandle) {
      if (newHandle && newHandle !== '') {
        this.loadBlog(newHandle);
      }
    },

    filtersExpanded: function(isExpanded) {
      if (isExpanded) {
        this.lockBodyScroll();
        this.addEscapeListener();
      } else {
        this.unlockBodyScroll();
        this.removeEscapeListener();
      }
    },


  },

  mounted() {
    document.addEventListener('click', event => {
      if (this.sortExpanded) {
        const sortToggle = event.target.closest('.oa-blog-sort-toggle');
        const sortPopup = event.target.closest('.oa-blog-sort-popup');
        if (!sortToggle && !sortPopup) {
          this.sortExpanded = false;
        }
      }
    });

    this.parseUrlParameters();

    axios.get('/search?view=blogs-data&q=__bootstrap__')
      .then(response => {
        const cats = (response.data && response.data.available_blogs) ? response.data.available_blogs : [];
        this.availableBlogs = cats;

        let defaultHandle = this.initialBlogHandle || this.blogHandle;
        if (!defaultHandle && cats.length > 0) defaultHandle = cats[0].handle;

        if (defaultHandle) {
          this.blogHandle = defaultHandle;
          this.loadBlog(defaultHandle);
        } else {
          this.blogData = null;
          this.articles = [];
          this.displayedArticles = [];
          this.blogTitle = 'Blog Articles';
          this.blogDescription = '';
          this.isShown = true;
          this.loading = false;
        }
      })
      .catch(err => {
        console.error('Error loading blog categories:', err);
        this.blogData = null;
        this.articles = [];
        this.displayedArticles = [];
        this.error = 'Failed to load blog categories.';
        this.isShown = true;
        this.loading = false;
      });
  }
};


(function () {
  const breakpoint = window.matchMedia('(min-width:992px)');
  let blogSwiper;

  function setup() {
    const swiperEl = document.getElementById("oa-blog-swiper");
    if (!swiperEl || swiperEl.className.indexOf('swiper-container-initialized') !== -1) {
      return false;
    }

    const nextEl = '.oa-blog-swiper .oa-product-slider__nav .oa-product-slider__nav-next';
    const prevEl = '.oa-blog-swiper .oa-product-slider__nav .oa-product-slider__nav-prev';

    blogSwiper = new Swiper(".oa-blog-swiper", {
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
        disabledClass: 'oa-product-slider__nav--disabled',
        hiddenClass: 'oa-product-slider__nav--disabled'
      },
      scrollbar: {
        el: '.oa-product-slider__scrollbar',
        dragClass: 'oa-product-slider__scrollbar-drag',
        enabled: true,
        dragSize: 200,
        draggable: true,
        snapOnRelease: true,
      },
      direction: 'horizontal',
      loop: false,
      freeMode: false,
      spaceBetween: 10,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
        invert: true
      },
      slidesPerView: 1.2,
      breakpoints: {
        600: {
          slidesPerView: 2,
          scrollbar: {
            dragSize: 200,
          },
          spaceBetween: 10,
        },
        900: {
          slidesPerView: 3,
          scrollbar: {
            dragSize: 200,
          },
          spaceBetween: 10,
        },
      },
    });
  }

  const breakpointChecker = function () {
    if (blogSwiper !== undefined) {
      blogSwiper.destroy(true, true);
    }
    return enableSwiper();
  };

  const enableSwiper = function() {
    setup();
  };

  breakpoint.addListener(breakpointChecker);

  window.addEventListener('DOMContentLoaded', (event) => {
    setTimeout(() => {
      breakpointChecker();
    }, 1000);
  });
})();

